import { combineReducers } from 'redux';
import produce from 'immer';
import {
  ActivityTemplateAction,
  ActivityTemplateActionType,
  ActivityTemplateEditingDetails,
} from '../../actions/activityTemplateActions';

const loading = (state = false, action: ActivityTemplateAction) => {
  switch (action.type) {
    case ActivityTemplateActionType.GET_ACTIVITY_TEMPLATES:
      return true;
    case ActivityTemplateActionType.GET_ACTIVITY_TEMPLATES_OK:
    case ActivityTemplateActionType.GET_ACTIVITY_TEMPLATES_ERROR:
      return false;
    default:
      return state;
  }
};

const loadingError = (state = '', action: ActivityTemplateAction) => {
  switch (action.type) {
    case ActivityTemplateActionType.GET_ACTIVITY_TEMPLATES:
      return '';
    case ActivityTemplateActionType.GET_ACTIVITY_TEMPLATES_ERROR:
      return action.error;
    default:
      return state;
  }
};

const creating = (state = false, action: ActivityTemplateAction) => {
  switch (action.type) {
    case ActivityTemplateActionType.CREATE_ACTIVITY_TEMPLATE:
      return true;
    case ActivityTemplateActionType.CREATE_ACTIVITY_TEMPLATE_OK:
    case ActivityTemplateActionType.CREATE_ACTIVITY_TEMPLATE_ERROR:
      return false;
    default:
      return state;
  }
};

const createError = (state = '', action: ActivityTemplateAction) => {
  switch (action.type) {
    case ActivityTemplateActionType.CREATE_ACTIVITY_TEMPLATE:
      return '';
    case ActivityTemplateActionType.CREATE_ACTIVITY_TEMPLATE_ERROR:
      return action.error;
    default:
      return state;
  }
};

type LoadingState = { loading: boolean; loadingError: string };
type DeletingState = { deleting: boolean; deletingError: string };
type EditingStates = {
  [editingStatusKey in ActivityTemplateEditingDetails['statusKey']]: boolean;
};
type EditingErrors = {
  [editingErrorKey in ActivityTemplateEditingDetails['errorKey']]: string;
};
type LoadingPackageMetadataState = {
  loadingPackageMetadata: boolean;
  loadingPackageMetadataError: string;
};
type LoadingPackageDownloadLinkState = {
  loadingPackageDownloadLink: boolean;
  loadingPackageDownloadLinkError: string;
};

export type ActivityTemplateState = LoadingState &
  DeletingState &
  EditingStates &
  EditingErrors &
  LoadingPackageMetadataState &
  LoadingPackageDownloadLinkState;

interface ActivityTemplateStatesById {
  [id: string]: ActivityTemplateState;
}

const byId = (state: ActivityTemplateStatesById = {}, action: ActivityTemplateAction) =>
  produce(state, (draft) => {
    switch (action.type) {
      case ActivityTemplateActionType.GET_ACTIVITY_TEMPLATES_OK: {
        action.data.forEach((stage) => {
          draft[stage.id] = {} as ActivityTemplateState;
        });

        break;
      }
      case ActivityTemplateActionType.CREATE_ACTIVITY_TEMPLATE_OK: {
        draft[action.data.id] = {} as ActivityTemplateState;
        break;
      }
      case ActivityTemplateActionType.GET_ACTIVITY_TEMPLATE: {
        draft[action.id] = {
          ...draft[action.id],
          loading: true,
          loadingError: '',
        };
        break;
      }
      case ActivityTemplateActionType.GET_ACTIVITY_TEMPLATE_OK: {
        draft[action.id].loading = false;
        break;
      }
      case ActivityTemplateActionType.GET_ACTIVITY_TEMPLATE_ERROR: {
        draft[action.id].loading = false;
        draft[action.id].loadingError = action.error;
        break;
      }
      case ActivityTemplateActionType.EDIT_ACTIVITY_TEMPLATE: {
        draft[action.id][action.statusKey] = true;
        draft[action.id][action.errorKey] = '';
        break;
      }
      case ActivityTemplateActionType.EDIT_ACTIVITY_TEMPLATE_OK: {
        draft[action.id][action.statusKey] = false;
        break;
      }
      case ActivityTemplateActionType.EDIT_ACTIVITY_TEMPLATE_ERROR: {
        draft[action.id][action.statusKey] = false;
        draft[action.id][action.errorKey] = action.error;
        break;
      }
      case ActivityTemplateActionType.DELETE_ACTIVITY_TEMPLATE: {
        draft[action.id].deleting = true;
        draft[action.id].deletingError = '';
        break;
      }
      case ActivityTemplateActionType.DELETE_ACTIVITY_TEMPLATE_OK: {
        delete draft[action.id];
        break;
      }
      case ActivityTemplateActionType.DELETE_ACTIVITY_TEMPLATE_ERROR: {
        draft[action.id].deleting = false;
        draft[action.id].deletingError = action.error;
        break;
      }
      default:
        return state;
    }
  });

export default combineReducers({
  loading,
  creating,
  loadingError,
  createError,
  byId,
});
